import {
  FRUIT_PRICE_WON_VALUES,
  FRUIT_PRODUCT_IDS,
  type FruitPricesByProduct,
  type FruitPriceWon,
  type FruitProductId,
  type FruitShopV2Phase,
  type FruitShopV2ViewerState,
} from "@cloudnest/fruit-shop-v2-engine";
import { useEffect, useRef, useState } from "react";
import { buildLobbyPath, navigateTo } from "../../router";
import { useGameEventsWS } from "../../shared/hooks/use-game-events-ws";
import { useAuth } from "../../shared/store/auth-store";
import { useLobby, useLobbyActions } from "../../shared/store/lobby-store";
import { getParticipantDisplayName } from "../../shared/utils/participants";
import {
  fruitShopMultiplayerActions,
  useFruitShopMultiplayerStore,
} from "./multiplayer-game-store";
import { resolveFruitShopRouteMode } from "./route-mode";
import "./styles/fruit-shop-tokens.css";

interface ProductMeta {
  readonly productId: FruitProductId;
  readonly label: string;
  readonly className: string;
}

type PriceDraft = Partial<Record<FruitProductId, FruitPriceWon>>;

const PRODUCTS: readonly ProductMeta[] = [
  { productId: "APPLE", label: "사과", className: "apple" },
  { productId: "GRAPE", label: "포도", className: "grape" },
  { productId: "KOREAN_MELON", label: "참외", className: "melon" },
  { productId: "TANGERINE", label: "감귤", className: "tangerine" },
  { productId: "STRAWBERRY", label: "딸기", className: "strawberry" },
  { productId: "WATERMELON", label: "수박", className: "watermelon" },
] as const;

const PHASE_STEPS: readonly {
  readonly phase: FruitShopV2Phase;
  readonly label: string;
  readonly help: string;
}[] = [
  { phase: "ITEM_WINDOW", label: "아이템 창", help: "비밀/교체" },
  { phase: "PRICE_SUBMISSION", label: "희망가 제출", help: "딜러룸" },
  { phase: "ROUND_REVEALED", label: "결과 공개", help: "최저가" },
  { phase: "EXTENSION_READY", label: "연장", help: "전원 동점" },
  { phase: "COMPLETED", label: "최종 공개", help: "총수입" },
] as const;

function getProductMeta(productId: FruitProductId): ProductMeta {
  return PRODUCTS.find((product) => product.productId === productId)!;
}

function formatWon(value: number): string {
  return `${new Intl.NumberFormat("ko-KR").format(value)}원`;
}

function buildPrices(
  productIds: readonly FruitProductId[],
  draft: PriceDraft,
): FruitPricesByProduct {
  return Object.fromEntries(
    productIds.map((productId) => [productId, draft[productId] ?? 1000]),
  ) as FruitPricesByProduct;
}

function getSelf(state: FruitShopV2ViewerState, myPlayerId: string | null) {
  return state.players.find((player) => player.playerId === myPlayerId) ?? null;
}

function ProductLabel({ productId }: { readonly productId: FruitProductId }) {
  const meta = getProductMeta(productId);
  return (
    <span className="fruitv2-product-label">
      <span className={`fruitv2-fruit-dot ${meta.className}`} />
      {meta.label}
    </span>
  );
}

function PhaseRail({
  currentPhase,
}: {
  readonly currentPhase: FruitShopV2Phase;
}) {
  const currentIndex = PHASE_STEPS.findIndex(
    (step) => step.phase === currentPhase,
  );

  return (
    <div className="fruitv2-phase-rail" data-testid="fruit-shop-v2-phase-rail">
      {PHASE_STEPS.map((step, index) => {
        const className =
          index < currentIndex
            ? "done"
            : step.phase === currentPhase
              ? "current"
              : "future";
        return (
          <div
            key={step.phase}
            className={`fruitv2-phase-step ${className}`}
            data-testid={`fruit-shop-v2-phase-${step.phase}`}
          >
            <strong>{step.label}</strong>
            <span>{step.help}</span>
          </div>
        );
      })}
    </div>
  );
}

function PlayerRail({
  state,
  myPlayerId,
  getPlayerName,
}: {
  readonly state: FruitShopV2ViewerState;
  readonly myPlayerId: string | null;
  readonly getPlayerName: (playerId: string) => string;
}) {
  function getSeatStatus(playerId: string): string {
    if (playerId === myPlayerId) return "current";
    if (state.currentRound.phase === "ITEM_WINDOW") {
      return state.currentRound.itemReadyPlayerIds.includes(playerId)
        ? "ready"
        : "waiting";
    }
    if (state.currentRound.phase === "PRICE_SUBMISSION") {
      return state.currentRound.priceSubmittedPlayerIds.includes(playerId)
        ? "ready"
        : "waiting";
    }
    if (state.currentRound.phase === "ROUND_REVEALED") {
      return state.currentRound.confirmedPlayerIds.includes(playerId)
        ? "ready"
        : "waiting";
    }
    return "idle";
  }

  return (
    <div
      className="fruitv2-player-rail"
      data-testid="fruit-shop-v2-player-rail"
    >
      {state.players.map((player) => {
        const displayName =
          player.playerId === myPlayerId
            ? `나 · ${getPlayerName(player.playerId)}`
            : getPlayerName(player.playerId);
        return (
          <span
            key={player.playerId}
            className={`fruitv2-seat ${getSeatStatus(player.playerId)}`}
            data-testid="fruit-shop-v2-player-seat"
            title={displayName}
          >
            <span className="fruitv2-seat-label">{displayName}</span>
          </span>
        );
      })}
    </div>
  );
}

function MarketGrid({ state }: { readonly state: FruitShopV2ViewerState }) {
  return (
    <section className="fruitv2-card" data-testid="fruit-shop-v2-market">
      <div className="fruitv2-section-head">
        <div>
          <div className="fruitv2-kicker">public market</div>
          <h2>판매 과일</h2>
        </div>
        <span className="fruitv2-pill">가격 비공개</span>
      </div>
      <div className="fruitv2-market">
        {PRODUCTS.map((product) => {
          const market = state.currentRound.market[product.productId];
          return (
            <div
              key={product.productId}
              className={`fruitv2-product-card ${product.className}`}
            >
              <div className="fruitv2-product-title">
                <span>{product.label}</span>
                <strong>{market.sellerCount}명</strong>
              </div>
              <span>판매자 수 공개 · 가격 비공개</span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ItemWindow({
  state,
  myPlayerId,
  isObserver,
}: {
  readonly state: FruitShopV2ViewerState;
  readonly myPlayerId: string | null;
  readonly isObserver: boolean;
}) {
  const me = getSelf(state, myPlayerId);
  const ownRights = me?.ownFruitRights ?? [];
  const ready = myPlayerId
    ? state.currentRound.itemReadyPlayerIds.includes(myPlayerId)
    : false;

  return (
    <div className="fruitv2-workspace">
      <section className="fruitv2-card fruitv2-action-focus">
        <div className="fruitv2-section-head">
          <div>
            <div className="fruitv2-kicker">item window</div>
            <h2>
              {isObserver
                ? "관전 중 · 아이템 선택 대기"
                : ready
                  ? "내 선택 완료"
                  : "내 아이템 선택"}
            </h2>
          </div>
          <span className="fruitv2-pill">
            {state.currentRound.itemReadyPlayerIds.length}/
            {state.players.length} 완료
          </span>
        </div>
        {isObserver ? (
          <p className="fruitv2-muted">
            관전자는 개인 판매권과 아이템 상태를 볼 수 없습니다.
          </p>
        ) : (
          <div className="fruitv2-rights">
            {ownRights.map((productId) => (
              <div className="fruitv2-right" key={productId}>
                <ProductLabel productId={productId} />
                <strong>판매권 유지</strong>
              </div>
            ))}
          </div>
        )}
        {!isObserver && ready && (
          <p className="fruitv2-muted">
            다른 플레이어가 아이템 창을 마치면 희망가 제출로 넘어갑니다.
          </p>
        )}
      </section>
      <MarketGrid state={state} />
    </div>
  );
}

function PriceSubmission({
  state,
  productIds,
  priceDraft,
  onSetPrice,
  isObserver,
}: {
  readonly state: FruitShopV2ViewerState;
  readonly productIds: readonly FruitProductId[];
  readonly priceDraft: PriceDraft;
  readonly onSetPrice: (
    productId: FruitProductId,
    priceWon: FruitPriceWon,
  ) => void;
  readonly isObserver: boolean;
}) {
  const submittedCount = state.currentRound.priceSubmittedPlayerIds.length;
  const submitted = state.currentRound.ownPriceSubmission !== null;

  return (
    <div className="fruitv2-workspace">
      <section className="fruitv2-card fruitv2-action-focus">
        <div className="fruitv2-section-head">
          <div>
            <div className="fruitv2-kicker">dealer room</div>
            <h2>
              {isObserver
                ? "관전 중 · 희망가 제출 진행"
                : submitted
                  ? "내 희망가 제출 완료"
                  : "내 희망가 제출"}
            </h2>
          </div>
          <span className="fruitv2-pill">
            {submittedCount}/{state.players.length} 제출
          </span>
        </div>
        {isObserver ? (
          <p className="fruitv2-muted">
            관전자는 제출 여부만 볼 수 있고, 개인 희망가와 가격 선택 UI는
            표시되지 않습니다.
          </p>
        ) : submitted ? (
          <p className="fruitv2-muted">
            모든 플레이어가 제출하면 라운드 결과가 자동 공개됩니다.
          </p>
        ) : (
          <div className="fruitv2-price-list">
            {productIds.map((productId) => (
              <div className="fruitv2-price-row" key={productId}>
                <ProductLabel productId={productId} />
                <div className="fruitv2-stepper">
                  {FRUIT_PRICE_WON_VALUES.map((priceWon) => (
                    <button
                      key={priceWon}
                      type="button"
                      className={
                        (priceDraft[productId] ?? 1000) === priceWon
                          ? "selected"
                          : ""
                      }
                      data-testid={`fruit-shop-v2-price-${productId}-${priceWon}`}
                      onClick={() => onSetPrice(productId, priceWon)}
                    >
                      {priceWon / 1000}천
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
      <MarketGrid state={state} />
    </div>
  );
}

function ResultTable({
  state,
  getPlayerName,
}: {
  readonly state: FruitShopV2ViewerState;
  readonly getPlayerName: (playerId: string) => string;
}) {
  return (
    <section className="fruitv2-card" data-testid="fruit-shop-v2-result-table">
      <div className="fruitv2-section-head">
        <div>
          <div className="fruitv2-kicker">round result</div>
          <h2>{state.currentRound.roundNumber}R 판매 결과</h2>
        </div>
        <span className="fruitv2-pill">누적 수입 비공개</span>
      </div>
      <div className="fruitv2-result-list">
        {state.currentRound.productResults.map((result) => {
          const product = getProductMeta(result.productId);
          return (
            <div
              key={result.productId}
              className={`fruitv2-result-row fruitv2-round-result-row ${
                result.hidden ? "hidden" : ""
              }`}
            >
              <ProductLabel productId={result.productId} />
              {result.hidden ? (
                <strong className="fruitv2-result-main">비밀로 비공개</strong>
              ) : (
                <span className="fruitv2-result-main">
                  {formatWon(result.finalPriceWon ?? 0)} ·{" "}
                  {formatWon(result.totalRevenueWon ?? 0)} ·{" "}
                  {(result.winnerIds ?? []).map(getPlayerName).join(", ")}
                </span>
              )}
              <span className={`fruitv2-fruit-dot ${product.className}`} />
            </div>
          );
        })}
      </div>
    </section>
  );
}

function RevealedRound({
  state,
  getPlayerName,
}: {
  readonly state: FruitShopV2ViewerState;
  readonly getPlayerName: (playerId: string) => string;
}) {
  return (
    <div className="fruitv2-workspace">
      <ResultTable state={state} getPlayerName={getPlayerName} />
    </div>
  );
}

function ExtensionGate() {
  return (
    <div
      className="fruitv2-workspace"
      data-testid="fruit-shop-v2-extension-ready"
    >
      <section className="fruitv2-card fruitv2-action-focus">
        <div className="fruitv2-kicker">extension</div>
        <h2>전원 동점 · 연장 준비</h2>
        <p>모든 플레이어 수입에서 10,000원을 차감했습니다.</p>
      </section>
    </div>
  );
}

function CompletedScreen({
  state,
  getPlayerName,
}: {
  readonly state: FruitShopV2ViewerState;
  readonly getPlayerName: (playerId: string) => string;
}) {
  const rankedPlayers = [...state.players].sort(
    (a, b) => (b.totalIncomeWon ?? 0) - (a.totalIncomeWon ?? 0),
  );

  return (
    <div className="fruitv2-completed" data-testid="fruit-shop-v2-final-reveal">
      <section className="fruitv2-card fruitv2-terminal-card">
        <div className="fruitv2-kicker">final reveal</div>
        <h1>최종 수입 공개</h1>
        <div className="fruitv2-result-list">
          {rankedPlayers.map((player) => (
            <div
              key={player.playerId}
              className="fruitv2-result-row fruitv2-final-result-row"
            >
              <strong className="fruitv2-result-player-name">
                {getPlayerName(player.playerId)}
              </strong>
              <span className="fruitv2-result-income">
                {formatWon(player.totalIncomeWon ?? 0)}
              </span>
              <span className="fruitv2-result-award">
                가넷 {state.garnetAwardsByPlayerId[player.playerId] ?? 0}
              </span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function SecretSheet({
  onCancel,
  onConfirm,
}: {
  readonly onCancel: () => void;
  readonly onConfirm: (productId: FruitProductId) => void;
}) {
  const [selectedProductId, setSelectedProductId] =
    useState<FruitProductId>("APPLE");

  return (
    <div className="fruitv2-sheet" data-testid="fruit-shop-v2-secret-sheet">
      <h3>비밀 대상</h3>
      <p>이번 라운드 결과에서 과일 1개의 판매가와 수입을 숨깁니다.</p>
      <div className="fruitv2-choice-list">
        {PRODUCTS.map((product) => (
          <button
            key={product.productId}
            type="button"
            className={`fruitv2-choice ${
              selectedProductId === product.productId ? "selected" : ""
            }`}
            onClick={() => setSelectedProductId(product.productId)}
          >
            <ProductLabel productId={product.productId} />
            <span>
              {selectedProductId === product.productId ? "선택됨" : "선택"}
            </span>
          </button>
        ))}
      </div>
      <div className="fruitv2-action-row two">
        <button type="button" className="fruitv2-button" onClick={onCancel}>
          취소
        </button>
        <button
          type="button"
          className="fruitv2-button caution"
          data-testid="fruit-shop-v2-secret-confirm"
          onClick={() => onConfirm(selectedProductId)}
        >
          비밀 사용 확정
        </button>
      </div>
    </div>
  );
}

function ChangeSheet({
  productIds,
  onCancel,
  onConfirm,
}: {
  readonly productIds: readonly FruitProductId[];
  readonly onCancel: () => void;
  readonly onConfirm: (
    fromProductId: FruitProductId,
    toProductId: FruitProductId,
  ) => void;
}) {
  const firstFrom = productIds[0] ?? "APPLE";
  const firstTo =
    FRUIT_PRODUCT_IDS.find((productId) => !productIds.includes(productId)) ??
    "GRAPE";
  const [selectedFromProductId, setSelectedFromProductId] =
    useState<FruitProductId>(firstFrom);
  const [selectedToProductId, setSelectedToProductId] =
    useState<FruitProductId>(firstTo);

  return (
    <div className="fruitv2-sheet" data-testid="fruit-shop-v2-change-sheet">
      <h3>판매권 교체</h3>
      <p>교체 기록은 모두에게 공개되고 게임 끝까지 유지됩니다.</p>
      <div className="fruitv2-choice-list">
        {productIds.map((productId) => (
          <button
            key={productId}
            type="button"
            className={`fruitv2-choice ${
              selectedFromProductId === productId ? "selected" : ""
            }`}
            onClick={() => setSelectedFromProductId(productId)}
          >
            <ProductLabel productId={productId} />
            <span>교체할 판매권</span>
          </button>
        ))}
        {FRUIT_PRODUCT_IDS.filter(
          (productId) => !productIds.includes(productId),
        ).map((productId) => (
          <button
            key={productId}
            type="button"
            className={`fruitv2-choice ${
              selectedToProductId === productId ? "selected" : ""
            }`}
            onClick={() => setSelectedToProductId(productId)}
          >
            <ProductLabel productId={productId} />
            <span>새 판매권</span>
          </button>
        ))}
      </div>
      <div className="fruitv2-action-row two">
        <button type="button" className="fruitv2-button" onClick={onCancel}>
          취소
        </button>
        <button
          type="button"
          className="fruitv2-button primary"
          data-testid="fruit-shop-v2-change-confirm"
          onClick={() => onConfirm(selectedFromProductId, selectedToProductId)}
        >
          교체 확정
        </button>
      </div>
    </div>
  );
}

interface FruitShopObserverViewProps {
  readonly viewerState: FruitShopV2ViewerState;
  readonly connectionLabel: string;
  readonly getPlayerName: (playerId: string) => string;
  readonly onLeave: () => void;
  readonly leaveLabel?: string;
}

export function FruitShopObserverView({
  viewerState,
  connectionLabel,
  getPlayerName,
  onLeave,
  leaveLabel = "나가기",
}: FruitShopObserverViewProps) {
  return (
    <div
      className="fruitv2-playground"
      data-testid="game-screen"
      data-game="fruit-shop"
    >
      <div className="fruitv2-app fruitv2-live-shell">
        <header className="fruitv2-app-top" data-testid="fruit-shop-v2-top-bar">
          <div className="fruitv2-titlebar">
            <div className="fruitv2-titlebar-title">
              <strong data-testid="fruit-shop-v2-round-indicator">
                과일가게 · {viewerState.currentRound.roundNumber}R
              </strong>
              <span>관전자 · {connectionLabel}</span>
            </div>
            <div className="fruitv2-titlebar-actions">
              <span
                className="fruitv2-pill"
                data-testid="fruit-shop-v2-observer-banner"
              >
                관전 중
              </span>
              <span className="fruitv2-pill">
                {viewerState.players.length}명
              </span>
              <button
                type="button"
                className="fruitv2-button"
                onClick={onLeave}
              >
                {leaveLabel}
              </button>
            </div>
          </div>

          <PhaseRail currentPhase={viewerState.currentRound.phase} />
          <PlayerRail
            state={viewerState}
            myPlayerId={null}
            getPlayerName={getPlayerName}
          />
        </header>

        <main className="fruitv2-body">
          {viewerState.currentRound.phase === "ITEM_WINDOW" && (
            <ItemWindow
              state={viewerState}
              myPlayerId={null}
              isObserver={true}
            />
          )}

          {viewerState.currentRound.phase === "PRICE_SUBMISSION" && (
            <PriceSubmission
              state={viewerState}
              productIds={[]}
              priceDraft={{}}
              onSetPrice={() => undefined}
              isObserver={true}
            />
          )}

          {viewerState.currentRound.phase === "ROUND_REVEALED" && (
            <RevealedRound state={viewerState} getPlayerName={getPlayerName} />
          )}

          {viewerState.currentRound.phase === "EXTENSION_READY" && (
            <ExtensionGate />
          )}

          {viewerState.currentRound.phase === "COMPLETED" && (
            <CompletedScreen
              state={viewerState}
              getPlayerName={getPlayerName}
            />
          )}
          <div className="fruitv2-bottom-spacer" />
        </main>

        <footer
          className="fruitv2-bottom"
          data-testid="fruit-shop-v2-bottom-bar"
        >
          <div className="fruitv2-bottom-meta">
            <span>관전 모드</span>
            <span>공개 정보만 표시</span>
          </div>
          <div
            className="fruitv2-action-row single"
            data-testid="fruit-shop-v2-observer-bottom"
          >
            <span className="fruitv2-pill">리플레이 읽기 전용</span>
          </div>
        </footer>
      </div>
    </div>
  );
}

export function GameScreen() {
  const { user } = useAuth();
  const { currentRoom } = useLobby();
  const lobbyActions = useLobbyActions();
  const { viewerState, lastError, myPlayerId, isObserver } =
    useFruitShopMultiplayerStore();
  const actions = fruitShopMultiplayerActions;
  const [priceDraft, setPriceDraft] = useState<PriceDraft>({});
  const [secretOpen, setSecretOpen] = useState(false);
  const [changeOpen, setChangeOpen] = useState(false);

  const roomId = currentRoom?.id ?? null;
  const isHost = currentRoom?.hostUserId === user?.id;
  const isRoomObserver = Boolean(
    user?.id &&
      (currentRoom?.observerUserIds ?? []).includes(user.id) &&
      !(currentRoom?.participantUserIds ?? []).includes(user.id),
  );
  const readOnlyObserver = isObserver || isRoomObserver;
  const actionsRef = useRef(actions);
  const lobbyActionsRef = useRef(lobbyActions);

  useEffect(() => {
    actionsRef.current = actions;
    lobbyActionsRef.current = lobbyActions;
  }, [lobbyActions]);

  const { connectionStatus } = useGameEventsWS(roomId, {
    onGameEvent: () => {},
    onViewerState: (state, seq) => {
      actionsRef.current.applyRemoteEvent(state, seq);
    },
    onRoomUpdated: (room) => {
      lobbyActionsRef.current.updateCurrentRoom(room);
    },
    onReconnect: () => {
      if (roomId) void actionsRef.current.replayEvents(roomId);
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: room id + host bit are the stable init boundaries; room_updated may refresh participant objects.
  useEffect(() => {
    if (!roomId || !currentRoom || !user) return;
    const participantIds = currentRoom.participantUserIds ?? [];
    if (participantIds.length === 0) return;

    if (readOnlyObserver) {
      void actions.initializeAsObserver(roomId);
    } else if (isHost) {
      void actions.initializeAsHost(
        roomId,
        participantIds,
        currentRoom.hostUserId,
      );
    } else {
      void actions.initializeAsGuest(roomId);
    }

    return () => {
      actions.reset();
    };
  }, [roomId, currentRoom?.id, user?.id, isHost, readOnlyObserver]);

  const draftResetKey = `${viewerState?.currentRound.roundNumber ?? 0}:${viewerState?.currentRound.phase ?? "loading"}`;
  useEffect(() => {
    if (draftResetKey.length > 0) {
      setPriceDraft({});
      setSecretOpen(false);
      setChangeOpen(false);
    }
  }, [draftResetKey]);

  const getPlayerName = (playerId: string): string =>
    getParticipantDisplayName(currentRoom, playerId, playerId);

  const me = viewerState ? getSelf(viewerState, myPlayerId) : null;
  const ownProductIds = me?.ownFruitRights ?? [];
  const ownPrice = viewerState?.currentRound.ownPriceSubmission ?? null;
  const ownReady =
    viewerState !== null &&
    myPlayerId !== null &&
    viewerState.currentRound.itemReadyPlayerIds.includes(myPlayerId);
  const canUseItem =
    me?.itemStatus === "AVAILABLE" &&
    viewerState?.status === "IN_PROGRESS" &&
    viewerState.currentRound.phase !== "ROUND_REVEALED";
  const canReady =
    viewerState?.currentRound.phase === "ITEM_WINDOW" && !ownReady;
  const canSubmitPrice =
    viewerState?.currentRound.phase === "PRICE_SUBMISSION" && ownPrice === null;
  const needsResultConfirm =
    viewerState?.currentRound.phase === "ROUND_REVEALED" &&
    myPlayerId !== null &&
    !viewerState.currentRound.confirmedPlayerIds.includes(myPlayerId);
  const canStartExtension =
    viewerState?.currentRound.phase === "EXTENSION_READY" && isHost;

  function leaveGame(): void {
    actions.reset();
    lobbyActions.leaveGame();
    navigateTo(buildLobbyPath("fruit-shop", resolveFruitShopRouteMode()));
  }

  if (!currentRoom) {
    return (
      <div className="fruitv2-playground" data-testid="fruit-shop-v2-screen">
        <div className="fruitv2-setup">
          <section className="fruitv2-card fruitv2-setup-card">
            <h1>과일가게</h1>
            <p>게임 방이 없습니다.</p>
            <button
              type="button"
              className="fruitv2-button primary"
              onClick={() =>
                navigateTo(
                  buildLobbyPath("fruit-shop", resolveFruitShopRouteMode()),
                )
              }
            >
              로비로 이동
            </button>
          </section>
        </div>
      </div>
    );
  }

  if (!viewerState) {
    return (
      <div
        className="fruitv2-playground"
        data-testid="fruit-shop-v2-screen"
        data-game="fruit-shop"
      >
        <div className="fruitv2-setup">
          <section className="fruitv2-card fruitv2-setup-card">
            <div className="fruitv2-kicker">loading private view</div>
            <h1>과일가게</h1>
            <p>
              {connectionStatus === "connected"
                ? "방장이 게임 상태를 준비 중입니다."
                : "게임 서버에 연결 중입니다."}
            </p>
            {lastError && (
              <div className="fruitv2-toast">{lastError.error}</div>
            )}
          </section>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fruitv2-playground"
      data-testid="game-screen"
      data-game="fruit-shop"
    >
      <div className="fruitv2-app fruitv2-live-shell">
        <header className="fruitv2-app-top" data-testid="fruit-shop-v2-top-bar">
          <div className="fruitv2-titlebar">
            <div className="fruitv2-titlebar-title">
              <strong data-testid="fruit-shop-v2-round-indicator">
                과일가게 · {viewerState.currentRound.roundNumber}R
              </strong>
              <span>
                {readOnlyObserver ? "관전자" : getPlayerName(myPlayerId ?? "")}{" "}
                · {connectionStatus}
              </span>
            </div>
            <div className="fruitv2-titlebar-actions">
              {readOnlyObserver && (
                <span
                  className="fruitv2-pill"
                  data-testid="fruit-shop-v2-observer-banner"
                >
                  관전 중
                </span>
              )}
              <span className="fruitv2-pill">
                {viewerState.players.length}명
              </span>
              <button
                type="button"
                className="fruitv2-button"
                onClick={leaveGame}
              >
                나가기
              </button>
            </div>
          </div>

          <PhaseRail currentPhase={viewerState.currentRound.phase} />
          <PlayerRail
            state={viewerState}
            myPlayerId={readOnlyObserver ? null : myPlayerId}
            getPlayerName={getPlayerName}
          />
        </header>

        <main className="fruitv2-body">
          {lastError && <div className="fruitv2-toast">{lastError.error}</div>}

          {viewerState.currentRound.phase === "ITEM_WINDOW" && (
            <ItemWindow
              state={viewerState}
              myPlayerId={myPlayerId}
              isObserver={readOnlyObserver}
            />
          )}

          {viewerState.currentRound.phase === "PRICE_SUBMISSION" && (
            <PriceSubmission
              state={viewerState}
              productIds={ownProductIds}
              priceDraft={priceDraft}
              onSetPrice={(productId, priceWon) =>
                setPriceDraft((draft) => ({ ...draft, [productId]: priceWon }))
              }
              isObserver={readOnlyObserver}
            />
          )}

          {viewerState.currentRound.phase === "ROUND_REVEALED" && (
            <RevealedRound state={viewerState} getPlayerName={getPlayerName} />
          )}

          {viewerState.currentRound.phase === "EXTENSION_READY" && (
            <ExtensionGate />
          )}

          {viewerState.currentRound.phase === "COMPLETED" && (
            <CompletedScreen
              state={viewerState}
              getPlayerName={getPlayerName}
            />
          )}
          <div className="fruitv2-bottom-spacer" />
        </main>

        <footer
          className="fruitv2-bottom"
          data-testid="fruit-shop-v2-bottom-bar"
        >
          {readOnlyObserver ? (
            <>
              <div className="fruitv2-bottom-meta">
                <span>관전 모드</span>
                <span>공개 정보만 표시</span>
              </div>
              <div
                className="fruitv2-action-row single"
                data-testid="fruit-shop-v2-observer-bottom"
              >
                <span className="fruitv2-pill">
                  플레이어 행동을 기다리는 중
                </span>
              </div>
            </>
          ) : viewerState.currentRound.phase === "ITEM_WINDOW" ? (
            <>
              <div className="fruitv2-bottom-meta">
                <span>게임 중 1회 아이템</span>
                <span>교체는 가격 전까지만</span>
              </div>
              <div className="fruitv2-action-row priority">
                <button
                  type="button"
                  className="fruitv2-button caution"
                  data-testid="fruit-shop-v2-open-secret"
                  onClick={() => setSecretOpen(true)}
                  disabled={!canReady || !canUseItem}
                >
                  비밀
                </button>
                <button
                  type="button"
                  className="fruitv2-button"
                  data-testid="fruit-shop-v2-open-change"
                  onClick={() => setChangeOpen(true)}
                  disabled={!canReady || !canUseItem}
                >
                  교체
                </button>
                <button
                  type="button"
                  className="fruitv2-button primary"
                  data-testid="fruit-shop-v2-ready-prices"
                  onClick={() =>
                    void actions.submitAction("ReadyForPriceSubmission")
                  }
                  disabled={!canReady}
                >
                  아이템 없이 진행
                </button>
              </div>
            </>
          ) : null}

          {!readOnlyObserver &&
            viewerState.currentRound.phase === "PRICE_SUBMISSION" && (
              <>
                <div className="fruitv2-bottom-meta">
                  <span>내 가격만 표시</span>
                  <span>제출 전 수정 가능</span>
                </div>
                <div className="fruitv2-action-row two">
                  <button
                    type="button"
                    className="fruitv2-button caution"
                    data-testid="fruit-shop-v2-open-secret"
                    onClick={() => setSecretOpen(true)}
                    disabled={!canUseItem || ownPrice !== null}
                  >
                    비밀
                  </button>
                  <button
                    type="button"
                    className="fruitv2-button primary"
                    data-testid="fruit-shop-v2-submit-prices"
                    onClick={() =>
                      void actions.submitAction("SubmitPrices", {
                        prices: buildPrices(ownProductIds, priceDraft),
                      })
                    }
                    disabled={!canSubmitPrice}
                  >
                    희망가 제출
                  </button>
                </div>
              </>
            )}

          {!readOnlyObserver &&
            viewerState.currentRound.phase === "ROUND_REVEALED" && (
              <div className="fruitv2-action-row single">
                <button
                  type="button"
                  className="fruitv2-button primary"
                  data-testid="fruit-shop-v2-confirm-result"
                  onClick={() =>
                    void actions.submitAction("ConfirmRoundResult")
                  }
                  disabled={!needsResultConfirm}
                >
                  결과 확인 완료
                </button>
              </div>
            )}

          {!readOnlyObserver &&
            viewerState.currentRound.phase === "EXTENSION_READY" && (
              <div className="fruitv2-action-row single">
                <button
                  type="button"
                  className="fruitv2-button primary"
                  data-testid="fruit-shop-v2-start-extension"
                  onClick={() =>
                    void actions.submitAction("StartExtensionRound")
                  }
                  disabled={!canStartExtension}
                >
                  연장 시작
                </button>
              </div>
            )}
        </footer>
      </div>

      {secretOpen && (
        <SecretSheet
          onCancel={() => setSecretOpen(false)}
          onConfirm={(productId) => {
            setSecretOpen(false);
            void actions.submitAction("UseSecret", { productId });
          }}
        />
      )}

      {changeOpen && (
        <ChangeSheet
          productIds={ownProductIds}
          onCancel={() => setChangeOpen(false)}
          onConfirm={(fromProductId, toProductId) => {
            setChangeOpen(false);
            void actions.submitAction("UseChange", {
              fromProductId,
              toProductId,
            });
          }}
        />
      )}
    </div>
  );
}
